import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Upload, FileText, Trash2, Search, Tag, Download, Share2, Plus, X, Mail, CheckSquare, Square } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Documents = ({ onLogout }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // [{file, name, description, product_tags, status}]
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [bulkTags, setBulkTags] = useState([]); // tags applied to all files in batch
  const fileInputRef = useRef(null);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [sharePhone, setSharePhone] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => { fetchDocuments(); fetchProducts(); }, []);
  useEffect(() => { fetchDocuments(); }, [searchQuery, selectedTag]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (searchQuery) params.q = searchQuery;
      if (selectedTag) params.product_tag = selectedTag;
      const r = await axios.get(`${API}/documents`, { params, headers: { Authorization: `Bearer ${token}` } });
      setDocuments(r.data);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(r.data);
    } catch {}
  };

  // ── Bulk Upload ─────────────────────────────────────────────────────────────
  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is too large (max 10MB)`); return false; }
      return true;
    });
    const newItems = valid.map(f => ({
      id: Math.random().toString(36).slice(2),
      file: f,
      name: f.name.replace(/\.[^/.]+$/, ''),
      description: '',
      product_tags: [...bulkTags],
      status: 'pending', // pending | uploading | done | error
    }));
    setUploadQueue(prev => [...prev, ...newItems]);
  };

  const updateQueueItem = (id, field, value) => {
    setUploadQueue(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeFromQueue = (id) => setUploadQueue(prev => prev.filter(item => item.id !== id));

  const handleBulkUpload = async () => {
    const pending = uploadQueue.filter(item => item.status === 'pending');
    if (!pending.length) { toast.error('No files to upload'); return; }
    setUploading(true);
    let success = 0, failed = 0;
    for (const item of pending) {
      updateQueueItem(item.id, 'status', 'uploading');
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('name', item.name.trim() || item.file.name);
        formData.append('description', item.description.trim());
        formData.append('product_tags', [...item.product_tags, ...bulkTags.filter(t => !item.product_tags.includes(t))].join(','));
        await axios.post(`${API}/documents`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        updateQueueItem(item.id, 'status', 'done');
        success++;
      } catch {
        updateQueueItem(item.id, 'status', 'error');
        failed++;
      }
    }
    setUploading(false);
    toast.success(`${success} uploaded${failed ? `, ${failed} failed` : ''}!`);
    if (success > 0) {
      fetchDocuments();
      setTimeout(() => {
        setUploadQueue([]);
        setBulkTags([]);
        setShowUploadModal(false);
      }, 1500);
    }
  };

  // ── Download ────────────────────────────────────────────────────────────────
  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted'); fetchDocuments();
    } catch { toast.error('Failed to delete'); }
  };

  // ── Share ───────────────────────────────────────────────────────────────────
  const openShareModal = (doc) => {
    setShareDoc(doc);
    setSharePhone('');
    setShareEmail('');
    setShareMessage(`Dear Doctor,\n\nPlease find the document "${doc.name}" shared by Synapse Health Tech.\n\nBest regards,\nSynapse Health Tech Team`);
    setShowShareModal(true);
  };

  const handleShareWhatsApp = () => {
    const phone = sharePhone.replace(/[^0-9]/g, '');
    if (!phone) { toast.error('Please enter a phone number'); return; }
    const docUrl = `${BACKEND_URL}/api/documents/${shareDoc.id}/download`;
    const msg = `${shareMessage}\n\nDocument: ${shareDoc.name}\nDownload: ${docUrl}`;
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    toast.success('WhatsApp opened!');
  };

  const handleShareEmail = () => {
    if (!shareEmail) { toast.error('Please enter an email address'); return; }
    const subject = encodeURIComponent(`Document: ${shareDoc.name} — Synapse Health Tech`);
    const body = encodeURIComponent(`${shareMessage}\n\nDocument: ${shareDoc.name}`);
    window.open(`mailto:${shareEmail}?subject=${subject}&body=${body}`, '_blank');
    toast.success('Email client opened!');
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('presentation') || fileType?.includes('powerpoint')) return '📊';
    return '📎';
  };

  const formatSize = (kb) => kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;

  const statusColor = (status) => ({
    pending: 'text-slate-400', uploading: 'text-teal-700 animate-pulse',
    done: 'text-emerald-600', error: 'text-red-500'
  }[status] || '');

  const statusLabel = (status) => ({
    pending: 'Ready', uploading: 'Uploading...', done: '✅ Done', error: '❌ Failed'
  }[status] || status);

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 md:p-8" data-testid="documents-page">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Document Repository
            </h1>
            <p className="text-sm text-slate-500 mt-1">Upload, search and share clinical papers, brochures & materials</p>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl px-5 py-2.5 font-semibold">
              <Upload className="mr-2 h-4 w-4" /> Upload Documents
            </Button>
          )}
        </div>

        {/* Search + Filter */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, description or tag..."
              className="pl-10 h-12 rounded-xl" />
          </div>
          <select value={selectedTag} onChange={e => setSelectedTag(e.target.value)}
            className="h-12 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-teal-500 min-w-[160px]">
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
        </div>

        {/* Documents Grid */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent" />
                <p className="text-slate-500 mt-4">Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-16">
                <div className="p-4 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full inline-block mb-4">
                  <FileText className="h-10 w-10 text-teal-700" />
                </div>
                <p className="text-slate-600 text-lg font-medium mb-2">
                  {searchQuery || selectedTag ? 'No documents match your search' : 'No documents yet'}
                </p>
                <p className="text-slate-400 mb-6">
                  {searchQuery || selectedTag ? 'Try a different search term' : 'Upload clinical papers, brochures and product materials'}
                </p>
                {isAdmin && !searchQuery && !selectedTag && (
                  <Button onClick={() => setShowUploadModal(true)}
                    className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl">
                    Upload First Document
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map(doc => (
                  <div key={doc.id}
                    className="group p-5 border border-slate-200 rounded-xl hover:border-teal-200 hover:shadow-md transition-all bg-white"
                    data-testid="document-card">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="text-3xl">{getFileIcon(doc.file_type)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800 truncate">{doc.name}</h3>
                        <p className="text-xs text-slate-400 mt-0.5">{doc.file_name} · {formatSize(doc.file_size_kb)}</p>
                      </div>
                    </div>
                    {doc.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{doc.description}</p>}
                    {doc.product_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {doc.product_tags.map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-100 rounded-lg">
                            <Tag className="h-3 w-3" />{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mb-4">By {doc.uploaded_by?.split('@')[0]}</p>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => handleDownload(doc)} variant="outline" size="sm"
                        className="flex-1 rounded-lg text-xs hover:border-teal-300 hover:text-teal-700">
                        <Download className="h-3.5 w-3.5 mr-1" /> Download
                      </Button>
                      <Button onClick={() => openShareModal(doc)} size="sm"
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg text-xs">
                        <Share2 className="h-3.5 w-3.5 mr-1" /> Share
                      </Button>
                      {isAdmin && (
                        <Button onClick={() => handleDelete(doc.id)} variant="ghost" size="sm"
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg px-2">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Bulk Upload Modal ─────────────────────────────────────────────── */}
        <Dialog open={showUploadModal} onOpenChange={open => {
          if (!uploading) { setShowUploadModal(open); if (!open) { setUploadQueue([]); setBulkTags([]); } }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Upload className="h-5 w-5 text-teal-700" /> Bulk Upload Documents
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4">

              {/* Drop Zone */}
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-teal-300 rounded-xl p-8 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50/30 transition-all">
                <input ref={fileInputRef} type="file" multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                  onChange={handleFilesSelect} className="hidden" />
                <Upload className="h-10 w-10 text-violet-400 mx-auto mb-3" />
                <p className="font-semibold text-slate-700">Click to select files or drag & drop</p>
                <p className="text-sm text-slate-400 mt-1">PDF, Word, PowerPoint, Images — max 10MB each</p>
                <p className="text-xs text-teal-700 mt-2">You can select multiple files at once</p>
              </div>

              {/* Bulk Tags — applied to all files */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Tag All Files to Product (Optional)</Label>
                <div className="flex gap-2">
                  <select value={tagInput} onChange={e => setTagInput(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-teal-500">
                    <option value="">Select product...</option>
                    {products.filter(p => !bulkTags.includes(p.name)).map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" onClick={() => {
                    if (tagInput && !bulkTags.includes(tagInput)) { setBulkTags(p => [...p, tagInput]); setTagInput(''); }
                  }} className="h-10 px-3 rounded-xl">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {bulkTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bulkTags.map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-sm px-3 py-1 bg-teal-100 text-teal-700 rounded-lg">
                        <Tag className="h-3 w-3" />{tag}
                        <button onClick={() => setBulkTags(p => p.filter(t => t !== tag))} className="ml-1 hover:text-red-500"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* File Queue */}
              {uploadQueue.length > 0 && (
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Files to Upload ({uploadQueue.length})
                  </Label>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {uploadQueue.map(item => (
                      <div key={item.id} className={`p-3 rounded-xl border ${item.status === 'done' ? 'border-emerald-200 bg-emerald-50' : item.status === 'error' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
                        <div className="flex items-start gap-2">
                          <span className="text-lg mt-0.5">{getFileIcon(item.file.type)}</span>
                          <div className="flex-1 min-w-0">
                            <Input value={item.name}
                              onChange={e => updateQueueItem(item.id, 'name', e.target.value)}
                              placeholder="Document name..."
                              disabled={item.status === 'done' || item.status === 'uploading'}
                              className="h-8 text-sm rounded-lg mb-1" />
                            <Input value={item.description}
                              onChange={e => updateQueueItem(item.id, 'description', e.target.value)}
                              placeholder="Description (optional)"
                              disabled={item.status === 'done' || item.status === 'uploading'}
                              className="h-8 text-xs rounded-lg" />
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className={`text-xs font-medium ${statusColor(item.status)}`}>
                              {statusLabel(item.status)}
                            </span>
                            {item.status === 'pending' && (
                              <button onClick={() => removeFromQueue(item.id)} className="text-slate-300 hover:text-red-500">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowUploadModal(false); setUploadQueue([]); setBulkTags([]); }}
                  disabled={uploading} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleBulkUpload}
                  disabled={uploading || uploadQueue.filter(i => i.status === 'pending').length === 0}
                  className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white">
                  {uploading ? 'Uploading...' : `Upload ${uploadQueue.filter(i => i.status === 'pending').length} File(s)`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Share Modal ───────────────────────────────────────────────────── */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Share2 className="h-5 w-5 text-emerald-600" />
                Share Document
              </DialogTitle>
            </DialogHeader>
            {shareDoc && (
              <div className="space-y-4 py-4">
                {/* Doc info */}
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-3">
                  <span className="text-2xl">{getFileIcon(shareDoc.file_type)}</span>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{shareDoc.name}</p>
                    <p className="text-xs text-slate-400">{formatSize(shareDoc.file_size_kb)}</p>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Message (editable)</Label>
                  <textarea value={shareMessage} onChange={e => setShareMessage(e.target.value)} rows={5}
                    className="w-full rounded-xl border border-slate-200 focus:border-emerald-400 focus:outline-none px-3 py-2 text-sm resize-none" />
                </div>

                {/* WhatsApp share */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-200 space-y-3">
                  <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                    📱 Share via WhatsApp
                  </p>
                  <Input value={sharePhone} onChange={e => setSharePhone(e.target.value)}
                    placeholder="Doctor's phone number (e.g. 9876543210)"
                    className="rounded-xl border-green-200 focus:border-green-500" />
                  <Button onClick={handleShareWhatsApp}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold">
                    📤 Open in WhatsApp
                  </Button>
                </div>

                {/* Email share */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3">
                  <p className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Share via Email
                  </p>
                  <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)}
                    placeholder="Doctor's email address"
                    type="email"
                    className="rounded-xl border-blue-200 focus:border-blue-500" />
                  <Button onClick={handleShareEmail}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold">
                    ✉️ Open in Email
                  </Button>
                </div>

                <Button variant="outline" onClick={() => setShowShareModal(false)} className="w-full rounded-xl">
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
};

export default Documents;
